import { Button, Container, Label, TextAreaInput, TextInput } from '@playcanvas/pcui';

import { Events } from '../events';

export type RfiRecord = {
    id: string;
    position: [number, number, number];
    title: string;
    text: string;
    imageUrl: string;
    /** Firebase Storage path for replace/delete (optional). */
    imagePath?: string;
    status: 'neutral' | 'resolved' | 'low' | 'high' | 'critical';
    tags: string[];
    cameraView: { position: number[]; target: number[] } | null;
    /** Volugraph Firestore sort order (slideshow / list). */
    orderIndex?: number;
    /** Annotation subtype — `coord` = control-point coordinate marker (separate from RFI pins). */
    kind?: 'rfi' | 'coord';
    /** Optional WGS84 coordinates (manual entry in Volugraph). */
    lat?: number;
    lng?: number;
    /** Optional elevation in meters (manual entry for coord markers). */
    ele?: number;
    /** True when this coord was placed as a map-plot GCP (alignment workflow). */
    usedAsGcp?: boolean;
};

/**
 * Floating panel for pinned RFIs (Request for Information) in the SuperSplat embed.
 */
class RfiPanel extends Container {
    private events: Events;
    private listContainer: Container;
    private titleInput: TextInput;
    private textInput: TextAreaInput;
    private imageInput: TextInput;
    private flyBtn: Button;
    private deleteBtn: Button;
    private saveBtn: Button;
    private selectedId: string | null = null;
    private rowButtons = new Map<string, Button>();
    private listRows: Container[] = [];

    constructor(events: Events) {
        super({ class: 'rfi-panel', hidden: true });

        this.events = events;

        this.append(new Label({ class: 'rfi-panel-title', text: 'Pinned RFIs' }));
        this.append(new Label({
            class: 'rfi-panel-hint',
            text: 'Select the RFI tool, then click the splat surface to place a pin. Add title, notes, and optional image URL.'
        }));

        this.listContainer = new Container({ class: 'rfi-pin-list' });
        this.append(this.listContainer);

        this.append(new Label({ text: 'Title', class: 'rfi-panel-hint' }));
        this.titleInput = new TextInput({ placeholder: 'RFI title', class: 'rfi-detail-input' });
        this.append(this.titleInput);

        this.append(new Label({ text: 'Description', class: 'rfi-panel-hint' }));
        this.textInput = new TextAreaInput({ placeholder: 'Details / question…', class: 'rfi-detail-textarea' });
        this.append(this.textInput);

        this.append(new Label({ text: 'Image URL (optional)', class: 'rfi-panel-hint' }));
        this.imageInput = new TextInput({ placeholder: 'https://…', class: 'rfi-detail-input' });
        this.append(this.imageInput);

        const actions = new Container({ class: 'rfi-actions' });
        this.flyBtn = new Button({ text: 'Fly to', class: 'rfi-action-fly' });
        this.saveBtn = new Button({ text: 'Save', class: 'rfi-action-save' });
        this.deleteBtn = new Button({ text: 'Delete', class: 'rfi-action-delete' });
        actions.append(this.flyBtn);
        actions.append(this.saveBtn);
        actions.append(this.deleteBtn);
        this.append(actions);

        this.flyBtn.dom.addEventListener('click', () => {
            if (this.selectedId) {
                this.events.fire('rfi.flyTo', this.selectedId);
            }
        });
        this.saveBtn.dom.addEventListener('click', () => {
            if (!this.selectedId) return;
            this.events.fire('rfi.saveDetail', {
                id: this.selectedId,
                title: this.titleInput.value?.trim() ?? '',
                text: this.textInput.value?.trim() ?? '',
                imageUrl: this.imageInput.value?.trim() ?? ''
            });
        });
        this.deleteBtn.dom.addEventListener('click', () => {
            if (this.selectedId) {
                this.events.fire('rfi.delete', this.selectedId);
            }
        });

        ['pointerdown', 'pointerup', 'pointermove', 'wheel'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });
    }

    setVisible(visible: boolean) {
        this.hidden = !visible;
    }

    rebuildList(records: RfiRecord[], selectedId: string | null) {
        this.listRows.forEach((row) => row.destroy());
        this.listRows = [];
        this.rowButtons.clear();
        records.forEach((r, index) => {
            const label = (r.title || `RFI ${index + 1}`).slice(0, 36);
            const row = new Container({ class: 'rfi-pin-row' });
            const btn = new Button({ text: label, class: 'rfi-pin-select' });
            btn.dom.addEventListener('click', () => {
                this.events.fire('rfi.select', r.id);
            });
            row.append(btn);
            this.listContainer.append(row);
            this.listRows.push(row);
            this.rowButtons.set(r.id, btn);
        });
        this.selectedId = selectedId;
        this.syncDetailInputs(records, selectedId);
        this.updateRowHighlight();
    }

    selectRecord(records: RfiRecord[], id: string | null) {
        this.selectedId = id;
        this.syncDetailInputs(records, id);
        this.updateRowHighlight();
    }

    private syncDetailInputs(records: RfiRecord[], id: string | null) {
        const rec = id ? records.find((x) => x.id === id) : null;
        this.titleInput.value = rec?.title ?? '';
        this.textInput.value = rec?.text ?? '';
        this.imageInput.value = rec?.imageUrl ?? '';
        this.flyBtn.enabled = !!rec && !!rec.cameraView;
        this.saveBtn.enabled = !!rec;
        this.deleteBtn.enabled = !!rec;
    }

    private updateRowHighlight() {
        this.rowButtons.forEach((btn, id) => {
            if (id === this.selectedId) {
                btn.dom.style.outline = '1px solid rgba(110, 179, 232, 0.7)';
            } else {
                btn.dom.style.outline = '';
            }
        });
    }
}

export { RfiPanel };
